import { ChangeEvent } from 'react';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { areLocalIdsEqual } from 'helpers/idComparison';
import {
  DocumentationViewItem,
  DocumentationViewItemType,
  isDirectoryItemType,
} from 'store/documentation/model/types';
import { DirectoryModel, DirectoryRoot } from 'shared/domain/directory/directoryModel';
import { DocumentState } from 'components/common/dataGrid/columnsFactory/types';
import { GridRowClassNameParams } from '@mui/x-data-grid-pro';

export const DOCUMENTATION_DND_TARGET_EVENT =
  'documentation:dnd-target-change';

export interface DocumentationDnDTargetEventDetail {
  id: string;
  name: string | null;
}

export function emitDocumentationDnDTargetChange(
  detail: DocumentationDnDTargetEventDetail
): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<DocumentationDnDTargetEventDetail>(
      DOCUMENTATION_DND_TARGET_EVENT,
      {
        detail,
      }
    )
  );
}

export const getRowId = (row: DocumentationViewItem): string =>
  row.uniqueKeyId ?? `${row.localId}-${row.type}`;

export const getRowClassName = (
  params: GridRowClassNameParams<DocumentationViewItem>
): string =>
  isArchived(params.row)
    ? DocumentState.archived
    : DocumentState.published;

export const handleFilesUpload = (
  files: FileList | null,
  onAdd: (files: File[], targetDirectoryId: string | null) => void,
  event: ChangeEvent<HTMLInputElement>
): void => {
  if (files) {
    onAdd(Array.from(files), null);
  }
  event.target.value = '';
};

export interface DeletableModel {
  deleted?: boolean;
}

export function isArchived(
  item: DeletableModel
): boolean {
  return item.deleted === true;
}

export const BulkActionMode = {
  RESTORE: 'restore',
  ARCHIVE: 'archive',
} as const;

export type BulkActionMode =
  (typeof BulkActionMode)[keyof typeof BulkActionMode];

export type BulkActionState = {
  mode: BulkActionMode;
  restoreDisabled: boolean;
};

export function getBulkActionState(
  items: Iterable<DocumentationViewItem>
): BulkActionState {
  let hasArchived = false;
  let hasActive = false;
  for (const item of items) {
    if (isArchived(item)) {
      hasArchived = true;
    } else {
      hasActive = true;
    }
    if (hasArchived && hasActive) {
      return { mode: BulkActionMode.RESTORE, restoreDisabled: true };
    }
  }
  if (hasArchived) {
    return { mode: BulkActionMode.RESTORE, restoreDisabled: false };
  }
  return { mode: BulkActionMode.ARCHIVE, restoreDisabled: false };
}

export function isRootDirectory(directoryId: string): boolean {
  return directoryId === '';
}

export function getCurrentDirectoryName(
  roots: DirectoryRoot[]
): string | null {
  const lastRoot = roots[roots.length - 1];
  const name = lastRoot?.name?.trim();
  return name && name.length > 0 ? name : null;
}

export function mapToViewItem(
  items: (DocumentationModel | DirectoryModel)[],
  type: DocumentationViewItemType
): DocumentationViewItem[] {
  return (items || []).map((item) => ({
    ...item,
    type,
    uniqueKeyId: `${item.localId}-${type}`,
  }));
}

/**
 * Position of a file row among the file rows only - the order of the gallery
 * files (directories are not in the gallery). -1 for directories or unknown rows.
 */
export function findGallerySlideOfRow(
  rows: DocumentationViewItem[],
  file: DocumentationViewItem
): number {
  if (isDirectoryItemType(file.type)) {
    return -1;
  }
  return rows
    .filter((row) => !isDirectoryItemType(row.type))
    .findIndex((row) => row === file || areLocalIdsEqual(row.localId, file.localId));
}

/**
 * A directory shown in a filtered listing only because it contains matching
 * files (the service worker marks such rows) - not because it matches itself.
 */
export function hasOnlyNestedFilterMatches(row: DocumentationViewItem): boolean {
  if (!isDirectoryItemType(row.type)) {
    return false;
  }
  const containsMatchingFiles = 'containsMatchingFiles' in row && row.containsMatchingFiles === true;
  const selfMatch = 'selfMatch' in row && row.selfMatch === true;
  return containsMatchingFiles && !selfMatch;
}
